/*
 * Copyright (c) 2025. Haodong JU
 */

import { hillo } from 'hillo'
import { cloudV2Url } from '@/model/dataLayer/api/SaaS/SaaSUtils'
import GlobalConfig from '@/model/global/GlobalSettings'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'
import dayjs from 'dayjs'
import { ageDisplay } from '@/model/dataLayer/api/Vip/UserDetail'
import { formatPrice } from '@/model/utils/formatPrice'

const entity = {
  // userId: {
  //   type: IKDataEntity.Types.Integer,
  //   form: false,
  //   requiredNew: false,
  //   header: false
  // },
  operations: {
    displayName: 'operation',
    type: IKDataEntity.Types.Integer,
    form: false,
    tableConfig: {
      overwrite: true
    }
  },
  username: {
    displayName: '用户名'
  },
  ageDisplay: {
    displayName: '年龄段',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: async () => { return await getAllUser() },
        itemText: 'ageDisplay',
        itemValue: 'ageDisplay'
      }
    },
    form: false
  },
  gender: {
    displayName: '性别',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: async () => { return await getAllUser() },
        itemText: 'gender',
        itemValue: 'gender'
      }
    },
    form: false
  },
  userLastConsumeAt: {
    type: IKDataEntity.Types.Date,
    formConfig: {
      type: {
        operation: 'date',
        needFilter: true
      }
    },
    displayName: '上次消费时间'
  },
  userTotalConsume: {
    type: IKDataEntity.Types.String,
    formConfig: {
      type: {
        name: 'text',
        operation: '>=',
        needFilter: true,
        hint: '筛选大于或等于输入值的'
      }
    },
    displayName: '最高消费'
  },
  currentPoints: {
    type: IKDataEntity.Types.String,
    formConfig: {
      type: {
        name: 'text',
        operation: '>=',
        hint: '筛选大于或等于输入值的',
        needFilter: true
      }
    },
    displayName: '积分'
  },
  labelIds: {
    displayName: '会员标签',
    type: IKDataEntity.Types.Option,
    formConfig: {
      type: {
        selectItems: async () => {
          const { getLabelsForBusinessLayer } = await import('@/model/dataLayer/api/MembershipSystem/MembershipLabel')
          const response = await getLabelsForBusinessLayer()
          return response?.labels || []
        },
        itemText: 'name',
        itemValue: 'id',
        multiple: true
      }
    }
  },
  userLevel: {
    displayName: '会员等级',
    type: IKDataEntity.Types.String,
    form: false
  }
}

const config = {
  load: async function (filter) {
    try {
      // Prepare query parameters for label filtering
      let queryParams = ''
      if (filter && filter.labelIds && filter.labelIds.length > 0) {
        queryParams = '?labelIds=' + filter.labelIds.join(',')
      }

      const response = await hillo.get(cloudV2Url + 'membership/user/business-layers/' + GlobalConfig.blId + '/members' + queryParams)

      return response.map(it => {
        // Ensure id is set
        if (!it.id && it.cloudUserInfo && it.cloudUserInfo.id) {
          it.id = it.cloudUserInfo.id
        }

        it.password = it.cloudUserInfo.password
        it.username = it.cloudUserInfo.username
        it.email = it.cloudUserInfo.email
        it.refUserId = it.cloudUserInfo.refUserId
        it.phoneNumber = it.cloudUserInfo.phoneNumber
        it.otp = it.cloudUserInfo.otp
        it.otpGeneratedAt = it.cloudUserInfo.otpGeneratedAt
        it.enabled = it.cloudUserInfo.enabled
        it.accountNonLocked = it.cloudUserInfo.accountNonLocked
        it.accountLockedAt = it.cloudUserInfo.accountLockedAt
        it.createTimestamp = it.cloudUserInfo.createTimestamp
        it.lastLoginAt = it.cloudUserInfo.lastLoginAt
        it.ageDisplay = ageDisplay(it.cloudUserInfo.birthday)
        it.gender = it.cloudUserInfo.gender
        it.userLastConsumeAt = it.currentMembership.userLastConsumeAt ? dayjs(it.currentMembership.userLastConsumeAt).format('YYYY-MM-DD HH:mm:ss') : '-'
        it.userTotalConsume = formatPrice(it.currentMembership.userTotalConsume)
        it.currentPoints = it.currentMembership.currentPoints
        // Include labels in the response if available
        it.labels = it.currentMembership.labels || []
        it.labelIds = it.labels.map(label => label && label.id ? label.id : null).filter(id => id !== null)
        // Include user level information if available
        it.userLevel = it.currentMembership.currentLevel?.displayInfo?.name || '-'
        return it
      })
    } catch (error) {
      console.error('Error in load function:', error)
      return []
    }
  }
}

export default IKDataEntity.ModelFactory(entity, config)

export async function editUserPoint (item) {
  return (await hillo.jsonPost(cloudV2Url + 'membership/user/adjustPoints', {
    ...item
  }))
}

export async function getAllUser () {
  try {
    const response = await hillo.get(cloudV2Url + 'membership/user/business-layers/' + GlobalConfig.blId + '/members')

    return response.map(it => {
      // Ensure id is set
      if (!it.id && it.cloudUserInfo && it.cloudUserInfo.id) {
        it.id = it.cloudUserInfo.id
      }

      it.password = it.cloudUserInfo.password
      it.username = it.cloudUserInfo.username
      it.email = it.cloudUserInfo.email
      it.refUserId = it.cloudUserInfo.refUserId
      it.phoneNumber = it.cloudUserInfo.phoneNumber
      it.otp = it.cloudUserInfo.otp
      it.otpGeneratedAt = it.cloudUserInfo.otpGeneratedAt
      it.enabled = it.cloudUserInfo.enabled
      it.accountNonLocked = it.cloudUserInfo.accountNonLocked
      it.accountLockedAt = it.cloudUserInfo.accountLockedAt
      it.createTimestamp = it.cloudUserInfo.createTimestamp
      it.lastLoginAt = it.cloudUserInfo.lastLoginAt
      it.ageDisplay = ageDisplay(it.cloudUserInfo.birthday)
      it.gender = it.cloudUserInfo.gender
      it.userLastConsumeAt = it.currentMembership.userLastConsumeAt ? dayjs(it.currentMembership.userLastConsumeAt).format('YYYY-MM-DD HH:mm:ss') : '-'
      it.userTotalConsume = it.currentMembership.userTotalConsume
      it.currentPoints = it.currentMembership.currentPoints
      // Include labels in the response if available
      it.labels = it.currentMembership.labels || []

      return it
    })
  } catch (error) {
    console.error('Error in getAllUser:', error)
    return []
  }
}

/**
 * Get detailed information about a user for a specific business layer, including their labels
 *
 * @param {string} userId - The user ID
 * @param {number} blId - The business layer ID (defaults to current business layer)
 * @returns {Promise<Object>} - Promise that resolves to the user details including labels
 */
export async function getUserBusinessLayerDetails (userId, blId = GlobalConfig.blId) {
  return await hillo.get(cloudV2Url + 'membership/user/users/' + userId + '/business-layers/' + blId + '/details', {})
}

/**
 * Get orders for a user with pagination
 *
 * @param {string} userId - The user ID
 * @param {number} page - The page number (default: 0)
 * @param {number} size - The page size (default: 20)
 * @returns {Promise<Object>} - Promise that resolves to an OrderResponseDTO containing orders and pagination info
 */
export async function getUserOrders (userId, page = 0, size = 20) {
  return await hillo.get(cloudV2Url + 'cloudOrders/user-orders/' + userId, { page, size })
}

/**
 * Get orders for a user by device ID
 *
 * @param {string} customerId - The customer ID
 * @param {number} deviceId - The device ID
 * @returns {Promise<Array>} - Promise that resolves to a list of OrderWithDetailDTO
 */
export async function getUserOrdersByDeviceId (customerId, deviceId) {
  return await hillo.get(cloudV2Url + 'cloudOrders/getUserOrdersByDevice/' + customerId + '/' + deviceId)
}
